import type { RowData } from '@tanstack/react-table';
import {
  type PropsWithChildren,
  useMemo,
  type ReactNode,
  Children,
} from 'react';

import type { DataTableColumnDef, DataTableRowData } from '../../public.api.js';
import type { DataTableOptions } from '../types/table.types.js';
import {
  isDataTableCellActions,
  type CollectedCellActions,
} from './CellActions.js';
import {
  isDataTableColumnActions,
  type CollectedColumnActions,
} from './ColumnActions.js';
import { isDataTableRowActions } from './RowActions.js';

/**
 * Configuration hook for the DataTable UserActions feature
 * @internal
 */
export function useUserActions(
  props: PropsWithChildren,
  options: DataTableOptions<RowData>,
  consumerColumnDefMap: Map<string, DataTableColumnDef<DataTableRowData>>,
): void {
  const configuredActions = useMemo(() => {
    const actions: {
      cellActions: CollectedCellActions<RowData>;
      columnActions: CollectedColumnActions<RowData>;
      rowActions?: (
        row: RowData,
        {
          rowDensity,
          rowIndex,
          rowId,
        }: {
          rowDensity: 'default' | 'condensed' | 'comfortable';
          rowIndex: number;
          rowId: string;
        },
      ) => ReactNode;
    } = {
      cellActions: [],
      columnActions: [],
      rowActions: undefined,
    };

    // Iterate through the children of the DataTable component in order to
    // find the `ColumnActions`, `CellActions` and `RowActions` definitions.
    Children.forEach(props.children, (child) => {
      // Check if the child is of type cell action and insert it into the actions
      // configuration.
      if (isDataTableCellActions<RowData>(child)) {
        // Add the action into the cell actions collection saved in the options.
        actions.cellActions.push({
          column: child.props.column,
          action: child.props.children,
        });
      }
      if (isDataTableColumnActions<RowData>(child)) {
        // Add the action into the column actions collection saved in the options.
        actions.columnActions.push({
          column: child.props.column,
          action: child.props.children,
        });
      }
      if (isDataTableRowActions(child)) {
        actions.rowActions = child.props.children;
      }
    });
    return actions;
  }, [props.children]);

  // oxlint-disable-next-line react/immutability
  options.consumerColumnDefMap = consumerColumnDefMap;
  // oxlint-disable-next-line react/immutability
  options.enableColumnActions = configuredActions.columnActions.length > 0;
  // oxlint-disable-next-line react/immutability
  options.columnActions = configuredActions.columnActions;

  // oxlint-disable-next-line react/immutability
  options.enableCellActions = configuredActions.cellActions.length > 0;
  // oxlint-disable-next-line react/immutability
  options.cellActions = configuredActions.cellActions;

  // oxlint-disable-next-line react/immutability
  options.enableRowActions = configuredActions.rowActions !== undefined;
  // oxlint-disable-next-line react/immutability
  options.rowActions = configuredActions.rowActions;
}
